import React, { useState } from 'react';
import { Rect, Group as KonvaGroup, Text } from 'react-konva';
import TextLabelShape from './TextLabelShape';
import ResizeAnchor from './ResizeAnchor';
import { hexToRgba } from '../utils/colorUtils';

const RectangleShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale,
  shapeStroke,
  onStartTextEdit,
  textEditingShapeId,
  onUpdateShape,
}) => {
  const [isDraggingCorner, setIsDraggingCorner] = useState(false);
  const [isDraggingText, setIsDraggingText] = useState(false);

  // Default text offset to right of rectangle if not stored
  const shapeWithDefaults = {
    ...shape,
    textOffsetX: shape.textOffsetX !== undefined ? shape.textOffsetX : shape.width + 5,
    textOffsetY: shape.textOffsetY !== undefined ? shape.textOffsetY : 0,
  };

  // Compute cursor based on draggability, consistent across anchors
  const anchorLeaveCursor = commonProps.draggable ? 'move' : 'pointer';

  // Handle top-left corner drag (moves entire shape)
  const handleTopLeftDrag = (e) => {
    if (!onUpdateShape) return;
    const node = e.target;
    const newX = shape.x + node.x();
    const newY = shape.y + node.y();

    onUpdateShape(shape.id, {
      x: newX,
      y: newY
    });

    // Reset anchor position
    node.position({ x: 0, y: 0 });
  };

  // Handle bottom-right corner drag (resizes rectangle)
  const handleBottomRightDrag = (e) => {
    if (!onUpdateShape) return;
    const node = e.target;
    const newWidth = Math.max(50, node.x());
    const newHeight = Math.max(50, node.y());

    onUpdateShape(shape.id, {
      width: newWidth,
      height: newHeight
    });
  };

  return (
    <KonvaGroup
      {...commonProps}
      draggable={commonProps.draggable && !isDraggingCorner && !isDraggingText}
    >
      <Rect
        width={shape.width}
        height={shape.height}
        stroke={hexToRgba(commonProps.stroke, 0.8)}
        fill={hexToRgba(shapeStroke, 0.05)}
        strokeWidth={commonProps.strokeWidth}
        listening={true}
        dash={[60, 10, 20, 10]}
        {...(isSelected
          ? {
            shadowColor: commonProps.shadowColor,
            shadowBlur: commonProps.shadowBlur,
            shadowOpacity: commonProps.shadowOpacity,
            shadowEnabled: commonProps.shadowEnabled,
          }
          : {})}
      />

      {/* Text label (title + body + anchor) — delegated to TextLabelShape */}
      <TextLabelShape
        shape={shapeWithDefaults}
        commonProps={commonProps}
        isSelected={isSelected}
        stageScale={stageScale}
        shapeStroke={shapeStroke}
        onStartTextEdit={onStartTextEdit}
        textEditingShapeId={textEditingShapeId}
        onUpdateShape={onUpdateShape}
      />

      {/* Control Points - Only visible when selected */}
      {isSelected && (
        <>
          <ResizeAnchor
            x={0}
            y={0}
            stageScale={stageScale}
            color={commonProps.stroke}
            cursor={anchorLeaveCursor}
            hoverCursor="move"
            onDragMove={handleTopLeftDrag}
            onDragStart={() => setIsDraggingCorner(true)}
            onDragEnd={() => setIsDraggingCorner(false)}
          />
          <ResizeAnchor
            x={shape.width}
            y={shape.height}
            stageScale={stageScale}
            color={commonProps.stroke}
            cursor={anchorLeaveCursor}
            hoverCursor="nwse-resize"
            onDragMove={handleBottomRightDrag}
            onDragStart={() => setIsDraggingCorner(true)}
            onDragEnd={() => setIsDraggingCorner(false)}
          />
        </>
      )}
    </KonvaGroup>
  );
};

export default RectangleShape;
